import * as React from "react"
import { Check, ChevronDown, Building2, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

interface Project {
  id: string;
  project_name: string | null;
  company_name: string | null;
}

interface ProjectSelectProps {
  options: Project[]
  value?: string
  onValueChange: (value: string) => void
  placeholder?: string
  className?: string
  id?: string
  disabled?: boolean
  onOpenChange?: (open: boolean) => void
  loading?: boolean
  onSearchChange?: (searchTerm: string) => void
}

export function ProjectSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select project",
  className,
  id,
  disabled,
  onOpenChange,
  loading = false,
  onSearchChange,
}: ProjectSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const filteredOptions = options.filter((option) => {
    const searchLower = search.toLowerCase()
    const projectName = option.project_name?.toLowerCase() || ''
    const companyName = option.company_name?.toLowerCase() || ''
    
    return (
      projectName.includes(searchLower) ||
      companyName.includes(searchLower)
    )
  })
  
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    onOpenChange?.(newOpen)
    if (!newOpen) {
      setSearch("")
      // Clear any pending search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    
    // Debounce the search API call
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(value.trim())
      }
    }, 300) // 300ms delay
  }

  // Handle clicking outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleOpenChange(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
    }
  }, [open])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const selectedProject = options.find(option => option.id === value)

  return (
    <div className="relative" ref={containerRef}>
      <Button
        id={id}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          "w-full justify-between",
          !value && "text-muted-foreground",
          className
        )}
        disabled={disabled}
        onClick={() => handleOpenChange(!open)}
        type="button"
      >
        {selectedProject ? (
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">
              {selectedProject.project_name || 'Unnamed Project'}
            </span>
          </div>
        ) : (
          placeholder
        )}
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div 
          className="absolute z-50 w-full bg-popover rounded-md border shadow-md mt-1 overflow-hidden"
        >
          <div className="p-2">
            <div className="flex items-center border-b mb-2">
              <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                placeholder="Search projects..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full bg-transparent border-0 outline-none focus:ring-0 text-sm p-0"
              />
            </div>
            <div 
              className="max-h-64 overflow-auto"
              onWheel={(e) => {
                // Allow wheel scrolling within this container
                e.stopPropagation();
              }}
            >
              {loading ? (
                <div className="py-6 text-center text-sm">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                  Loading projects...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm">
                  {options.length === 0 ? "No projects available." : "No projects found."}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredOptions.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => {
                        onValueChange(project.id)
                        handleOpenChange(false)
                      }}
                      className={cn(
                        "relative flex cursor-pointer select-none items-start rounded-md border border-border p-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                        value === project.id && "bg-accent text-accent-foreground"
                      )}
                      role="option"
                      aria-selected={value === project.id}
                    >
                      <div className="flex flex-1 flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {project.project_name || 'Unnamed Project'}
                          </span>
                          {value === project.id && (
                            <Check className="ml-auto h-4 w-4 text-primary" />
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {project.company_name || 'No company name'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 